// useDockEdgeAutoscroll — edge-автоскролл горизонтального ряда чипов «Дока
// действий» во время активного drag карточки.
//
// Проблема: на mobile при перетаскивании палец занят — проскроллить ряд чипов
// влево-вправо нельзя, и до дальних коллекций (>2) не дотянуться. Решение: пока
// идёт drag, слушаем глобальные pointer/touch-move; если палец/курсор над
// скролл-контейнером по Y И у левого/правого края по X — запускаем rAF-loop,
// плавно инкрементящий scrollLeft (скорость нарастает по близости к краю). В
// центральной зоне или вне дока — loop останавливается.
//
// Слушатель и rAF навешиваются ТОЛЬКО на время drag (item != null) и снимаются
// на end/cancel/unmount. Drop-логика и droppable-хайлайт чипов не затрагиваются —
// мы лишь читаем clientX/clientY и крутим scrollLeft контейнера.

import { type RefObject, useEffect, useRef } from "react";
import { useDragSessionStore } from "@/stores/drag-session";

/** Ширина edge-зоны у каждого края контейнера (px). */
const EDGE_PX = 56;
/** Скорость скролла на самой кромке (px/кадр); у внутренней границы edge-зоны → ~0. */
const MAX_SPEED_PX = 14;

/**
 * Навешивает edge-автоскролл на горизонтальный скролл-контейнер дока.
 *
 * @param scrollRef ref на контейнер с `overflow-x-auto` (ряд чипов).
 * @param enabled  включить фичу (по умолчанию — всегда; на desktop безвредно).
 */
export function useDockEdgeAutoscroll(
	scrollRef: RefObject<HTMLElement | null>,
	enabled = true,
): void {
	const isDragging = useDragSessionStore((s) => s.item != null);
	// Знаковая скорость текущего кадра: <0 — влево, >0 — вправо, 0 — стоп.
	const speedRef = useRef(0);
	const rafRef = useRef<number | null>(null);

	useEffect(() => {
		if (!enabled || !isDragging) return;
		const el = scrollRef.current;
		if (!el) return;

		function tick(): void {
			const node = scrollRef.current;
			if (node && speedRef.current !== 0) {
				node.scrollLeft += speedRef.current;
			}
			rafRef.current = requestAnimationFrame(tick);
		}

		function computeSpeed(clientX: number, clientY: number): void {
			const node = scrollRef.current;
			if (!node) {
				speedRef.current = 0;
				return;
			}
			const rect = node.getBoundingClientRect();
			// Курсор/палец должен быть в вертикальных границах дока, иначе стоп.
			const insideY = clientY >= rect.top && clientY <= rect.bottom;
			if (!insideY) {
				speedRef.current = 0;
				return;
			}
			const distLeft = clientX - rect.left;
			const distRight = rect.right - clientX;
			if (distLeft >= 0 && distLeft < EDGE_PX) {
				// У левого края — скроллим влево, скорость растёт к самой кромке.
				const factor = (EDGE_PX - distLeft) / EDGE_PX;
				speedRef.current = -MAX_SPEED_PX * factor;
			} else if (distRight >= 0 && distRight < EDGE_PX) {
				// У правого края — скроллим вправо.
				const factor = (EDGE_PX - distRight) / EDGE_PX;
				speedRef.current = MAX_SPEED_PX * factor;
			} else {
				speedRef.current = 0;
			}
		}

		function onPointerMove(e: PointerEvent): void {
			computeSpeed(e.clientX, e.clientY);
		}
		function onTouchMove(e: TouchEvent): void {
			const touch = e.touches[0];
			if (touch) computeSpeed(touch.clientX, touch.clientY);
		}

		// passive: drag уже сам управляет preventDefault — нам нужно только читать координаты.
		window.addEventListener("pointermove", onPointerMove, { passive: true });
		window.addEventListener("touchmove", onTouchMove, { passive: true });
		rafRef.current = requestAnimationFrame(tick);

		return () => {
			window.removeEventListener("pointermove", onPointerMove);
			window.removeEventListener("touchmove", onTouchMove);
			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
			speedRef.current = 0;
		};
	}, [enabled, isDragging, scrollRef]);
}
